<template>
  <v-container class="mt-10">
    <v-row>
      <v-col>
        <p class="text-h5 text-sm-h4 mb-1">Materials</p>
        <p class="text-body-2 mb-0">
          Each material stores its refractive index n; the simulators use ε = n². Pick them in the
          <b>Material</b> panel of the simulator (select a geometry, or nothing for the cell background).
        </p>
      </v-col>
    </v-row>

    <p class="text-subtitle-1 mt-4 mb-1">Ready-to-use materials</p>
    <p class="text-caption mb-2">
      Typical values near λ = 1.55 µm. Available to everyone and read-only. The solvers do not model
      dispersion or absorption, so metals are not included.
    </p>
    <v-row dense>
      <v-col v-for="material in builtinMaterials" :key="material._id" cols="12" sm="6" md="4" lg="3">
        <material-card :material="material" />
      </v-col>
    </v-row>

    <p class="text-subtitle-1 mt-6 mb-1">Your materials</p>
    <v-row v-if="!loading && userMaterials.length < 1" justify="center">
      <v-col cols="12" sm="8" md="6" class="text-center">
        <img src="/img/illustrations/folder.svg" width="100" class="mb-3" alt="" />
        <p class="text-h6 mb-1">You have no materials of your own yet</p>
        <p class="text-body-2">
          Create one inside a simulation: open the <b>Material</b> panel and press
          <v-icon small>mdi-plus</v-icon>, then give it a name and its refractive index n.
        </p>
        <v-btn to="/dashboard" class="mt-2 materials-empty-btn" color="primary">
          Go to my simulations
        </v-btn>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col v-for="material in userMaterials" :key="material._id" cols="12" sm="6" md="4" lg="3">
        <material-card :material="material" deletable @delete="deleteMaterial(material)" />
      </v-col>
    </v-row>

    <v-dialog max-width="400" v-model="deleteModal">
      <v-card>
        <v-card-title class="text-h6">Delete “{{ currentMaterial && currentMaterial.title }}”?</v-card-title>
        <v-card-text>
          Geometries that already use it keep their ε. You can create the material again later.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteModal = false">Cancel</v-btn>
          <v-btn text color="error" :loading="deleting" @click="confirmDeleteModal">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import materials from "@/services/materials.js";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
export default {
  name: "Materials",
  components: {
    MaterialCard: () => import("./MaterialCard")
  },
  data: () => ({
    materials: [],
    deleting: false,
    editedIndex: null,
    dialog: false,
    loading: true,
    deleteModal: false,
    currentMaterial: ""
  }),
  watch: {
    editedIndex: function() {
      this.dialog = !this.dialog;
    }
  },
  mounted: function() {
    this.getData();
    this.setPageInfo();
  },
  computed: {
    ...mapGetters("simulator", ["author"]),
    builtinMaterials() {
      return this.materials.filter(material => material.builtin);
    },
    userMaterials() {
      return this.materials.filter(material => !material.builtin);
    }
  },
  methods: {
    setPageInfo: function() {
      document.title = "Your materials - ElectrosFI";
      document
        .querySelector('meta[name="description"')
        ?.setAttribute(
          "content",
          "Simulate and edit photonic tools online - ElectrosFI"
        );
    },
    deleteMaterial(material) {
      this.currentMaterial = material;
      this.deleteModal = true;
    },
    async confirmDeleteModal() {
      this.deleting = true;
      try {
        const { data } = await materials.delete(this.currentMaterial._id);
        if (data && data.err) throw new Error(data.err);
        this.deleteModal = false;
        await this.getData();
      } catch (err) {
        Swal.fire({ title: "Could not delete the material", text: err.message });
      } finally {
        this.deleting = false;
      }
    },
    async getData() {
      const user = window.firebase && window.firebase.auth().currentUser;
      try {
        const { data } = await materials.get("/", { params: { author: user ? user.uid : this.author } });
        this.materials = Array.isArray(data) ? data : [];
      } catch (err) {
        this.materials = [];
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
<style>
#loading {
  height: 100vh;
}
</style>

<style lang="scss">
// Texto longo: no celular o botão quebra linha em vez de estourar a largura da tela
.materials-empty-btn {
  max-width: 100%;
  height: auto !important;
  min-height: 44px;
  padding-top: 8px !important;
  padding-bottom: 8px !important;

  .v-btn__content {
    white-space: normal;
    flex-shrink: 1;
  }
}
</style>
